import React from 'react';

export type NullableProps<T> = { [K in keyof T]: T[K] | null };

export type NonNullableProps<T> = { [K in keyof T]: NonNullable<T[K]> };

export type Nullish = null | undefined;

export type ObjectValues<T> = T[keyof T];

export type NotNullish<T> = T extends Nullish ? never : T;

/**
 * Make marked properties/fields optional
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Type guard for narrowing a value that could be nullish to nullish.
 */
export function isNullish<T>(value: Nullish | T): value is Nullish {
	return value === null || value === undefined;
}

/**
 * Type guard for eliminating null and undefined from a type.
 *
 * Especially useful for array filters.
 */
export function isNotNullish<T>(value: Nullish | T): value is T {
	return !isNullish(value);
}

/**
 * Use instead of `React.memo` to fix generic component props
 * not inferring generic types
 *
 * Issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
 *
 * @example
 * ```tsx
 * interface ComponentProps<T> {
 *    data: T[];
 *    mapItem: T => MappedItem<T>;
 * }
 *
 * const Component = () => {...};
 *
 * export default genericMemo(Component);
 * ```
 */
export const genericMemo: <T>(
	component: T,
	propsAreEqual?: Parameters<typeof React.memo>[1],
) => T = React.memo;

/** Usage:
interface UserSettings {
  username: string;
  email: string;
  notificationsEnabled: boolean;
  age: number;
  createdAt: number;
}
Get keys where the property type is a string
type StringKeys = KeysByValueType<UserSettings, string>;
type StringKeys = "username" | "email"
*/
export type KeysByValueType<T, U> = {
	[K in keyof T]: T[K] extends U ? K : never;
}[keyof T] &
	(string | number | symbol);
